import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Panel,
  Group as PanelGroup,
  Separator as PanelResizeHandle,
} from 'react-resizable-panels';
import Layout from 'components/ui/Layout';
import { updateCmsSchema } from 'features/cms/schemas/cms.schema';
import { useCmsPage } from 'features/cms/hooks/useCms';
import { useUpdateCmsPage } from 'features/cms/hooks/useCmsMutations';
import {
  PAGE_TYPE_OPTIONS,
  STATUS_OPTIONS,
  LANGUAGE_OPTIONS,
} from 'features/cms/types/cms.types';
import { ArrowLeft, Eye, Save, Loader2 } from 'lucide-react';

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ align: [] }],
    ['link', 'image'],
    ['clean'],
  ],
};

const EditCmsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeLanguage, setActiveLanguage] = useState('english');
  const updateCmsMutation = useUpdateCmsPage();

  // Fetch CMS page data
  const { data, isPending, isError } = useCmsPage(id);
  const cmsPage = data?.data;
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(updateCmsSchema),
    defaultValues: {
      slug: '',
      pageType: 'terms_and_conditions',
      status: 'draft',
      isPublished: false,
      title: {
        english: '',
        hindi: '',
        marwari: '',
      },
      content: {
        english: '',
        hindi: '',
        marwari: '',
      },
      shortDescription: {
        english: '',
        hindi: '',
        marwari: '',
      },
      seo: {
        metaTitle: '',
        metaDescription: '',
        metaKeywords: [],
        ogImage: '',
        ogTitle: '',
        ogDescription: '',
      },
    },
  });

  // Populate form when data loads
  useEffect(() => {
    if (cmsPage) {
      reset({
        slug: cmsPage.slug || '',
        pageType: cmsPage.pageType || 'terms_and_conditions',
        status: cmsPage.status || 'draft',
        isPublished: cmsPage.isPublished || false,
        title: {
          english: cmsPage.title?.english || '',
          hindi: cmsPage.title?.hindi || '',
          marwari: cmsPage.title?.marwari || '',
        },
        content: {
          english: cmsPage.content?.english || '',
          hindi: cmsPage.content?.hindi || '',
          marwari: cmsPage.content?.marwari || '',
        },
        shortDescription: {
          english: cmsPage.shortDescription?.english || '',
          hindi: cmsPage.shortDescription?.hindi || '',
          marwari: cmsPage.shortDescription?.marwari || '',
        },
        seo: {
          metaTitle: cmsPage.seo?.metaTitle || '',
          metaDescription: cmsPage.seo?.metaDescription || '',
          metaKeywords: cmsPage.seo?.metaKeywords || [],
          ogImage: cmsPage.seo?.ogImage || '',
          ogTitle: cmsPage.seo?.ogTitle || '',
          ogDescription: cmsPage.seo?.ogDescription || '',
        },
      });
    }
  }, [cmsPage, reset]);

  const watchContent = watch(`content.${activeLanguage}`);
  const watchTitle = watch(`title.${activeLanguage}`);

  const onSubmit = (data) => {
    updateCmsMutation.mutate(
      { cmsId: id, cmsData: data },
      {
        onSuccess: () => {
          navigate('/cms');
        },
      },
    );
  };

  if (isPending) {
    return (
      <Layout>
        <div className='min-h-screen bg-background flex items-center justify-center'>
          <div className='flex flex-col items-center gap-4'>
            <Loader2 size={48} className='animate-spin text-primary' />
            <p className='text-muted-foreground'>Loading CMS page...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (isError || !cmsPage) {
    return (
      <Layout>
        <div className='min-h-screen bg-background flex items-center justify-center'>
          <div className='text-center'>
            <p className='text-red-400 text-lg mb-4'>Failed to load CMS page</p>
            <button
              onClick={() => navigate('/cms')}
              className='px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90'
            >
              Back to CMS
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className='min-h-screen bg-background'>
        {/* Fixed Header */}
        <div className='sticky top-0 z-10 bg-card border-b border-border'>
          <div className='px-6 py-4'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-4'>
                <button
                  onClick={() => navigate('/cms')}
                  className='p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded transition-colors'
                  title='Back to CMS list'
                >
                  <ArrowLeft size={20} />
                </button>
                <div>
                  <h3 className='text-lg font-semibold text-foreground'>
                    Edit CMS Page
                  </h3>
                  <p className='text-sm text-muted-foreground'>
                    Update content page
                  </p>
                </div>
              </div>
              <div className='flex items-center gap-3'>
                <button
                  type='button'
                  onClick={() => navigate('/cms')}
                  className='px-4 py-2 text-sm border border-border text-foreground bg-card hover:bg-accent rounded-md transition-colors'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  form='cms-form'
                  disabled={updateCmsMutation.isPending}
                  className='flex items-center gap-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50'
                >
                  <Save size={16} />
                  {updateCmsMutation.isPending ? 'Updating...' : 'Update Page'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content with Resizable Panels */}
        <form
          id='cms-form'
          onSubmit={handleSubmit(onSubmit)}
          className='h-[calc(100vh-5rem)]'
        >
          <PanelGroup direction='horizontal'>
            {/* Left Panel - Editor */}
            <Panel defaultSize={50} minSize={30}>
              <div className='h-full overflow-y-auto p-6'>
                <div className='max-w-4xl mx-auto space-y-6'>
                  {/* Basic Information */}
                  <div className='bg-card border border-border rounded-lg p-6 space-y-4'>
                    <h4 className='text-md font-semibold text-foreground mb-4'>
                      Basic Information
                    </h4>

                    {/* Slug */}
                    <div>
                      <label className='block text-sm font-medium text-foreground mb-1'>
                        Slug <span className='text-red-400'>*</span>
                      </label>
                      <input
                        {...register('slug')}
                        type='text'
                        placeholder='e.g., terms-and-conditions'
                        className='w-full px-3 py-2 bg-input border border-border rounded-md text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent'
                      />
                      {errors.slug && (
                        <p className='text-red-400 text-xs mt-1'>
                          {errors.slug.message}
                        </p>
                      )}
                    </div>

                    {/* Page Type */}
                   

                    {/* Status */}
                    <div className='grid grid-cols-2 gap-4'>
                      <div>
                        <label className='block text-sm font-medium text-foreground mb-1'>
                          Status
                        </label>
                        <select
                          {...register('status')}
                          className='w-full px-3 py-2 bg-input border border-border rounded-md text-foreground focus:ring-2 focus:ring-primary focus:border-transparent'
                        >
                          {STATUS_OPTIONS.filter(
                            (opt) => opt.value !== 'all',
                          ).map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className='block text-sm font-medium text-foreground mb-1'>
                          Published
                        </label>
                        <div className='flex items-center h-10'>
                          <input
                            {...register('isPublished')}
                            type='checkbox'
                            className='w-4 h-4 text-primary bg-input border-border rounded focus:ring-primary'
                          />
                          <span className='ml-2 text-sm text-muted-foreground'>
                            Publish this page
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Language Tabs */}
                  <div className='bg-card border border-border rounded-lg p-6'>
                    <div className='flex gap-2 mb-4 border-b border-border'>
                      {LANGUAGE_OPTIONS.map((lang) => (
                        <button
                          key={lang.value}
                          type='button'
                          onClick={() => setActiveLanguage(lang.value)}
                          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                            activeLanguage === lang.value
                              ? 'border-primary text-primary'
                              : 'border-transparent text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {lang.label}
                        </button>
                      ))}
                    </div>

                    {/* Title */}
                    <div className='mb-4'>
                      <label className='block text-sm font-medium text-foreground mb-1'>
                        Title (
                        {
                          LANGUAGE_OPTIONS.find(
                            (l) => l.value === activeLanguage,
                          )?.label
                        }
                        )<span className='text-red-400'>*</span>
                      </label>
                      <input
                        {...register(`title.${activeLanguage}`)}
                        type='text'
                        placeholder={`Enter title in ${LANGUAGE_OPTIONS.find((l) => l.value === activeLanguage)?.label}`}
                        className='w-full px-3 py-2 bg-input border border-border rounded-md text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent'
                      />
                      {errors.title?.[activeLanguage] && (
                        <p className='text-red-400 text-xs mt-1'>
                          {errors.title[activeLanguage].message}
                        </p>
                      )}
                    </div>

                    {/* Content Editor */}
                    <div className='mb-4'>
                      <label className='block text-sm font-medium text-foreground mb-1'>
                        Content (
                        {
                          LANGUAGE_OPTIONS.find(
                            (l) => l.value === activeLanguage,
                          )?.label
                        }
                        )<span className='text-red-400'>*</span>
                      </label>
                      {/* English Editor */}
                      {activeLanguage === 'english' && (
                        <Controller
                          name='content.english'
                          control={control}
                          render={({ field }) => (
                            <div className='bg-white rounded-md'>
                              <ReactQuill
                                theme='snow'
                                value={field.value || ''}
                                onChange={field.onChange}
                                modules={quillModules}
                                formats={[
                                  'header',
                                  'bold',
                                  'italic',
                                  'underline',
                                  'strike',
                                  'list',
                                  'bullet',
                                  'indent',
                                  'align',
                                  'link',
                                  'image',
                                ]}
                                placeholder='Start typing your content here...'
                                className='[&_.ql-container]:min-h-[300px]'
                              />
                            </div>
                          )}
                        />
                      )}
                      {/* Hindi Editor */}
                      {activeLanguage === 'hindi' && (
                        <Controller
                          name='content.hindi'
                          control={control}
                          render={({ field }) => (
                            <div className='bg-white rounded-md'>
                              <ReactQuill
                                theme='snow'
                                value={field.value || ''}
                                onChange={field.onChange}
                                modules={quillModules}
                                formats={[
                                  'header',
                                  'bold',
                                  'italic',
                                  'underline',
                                  'strike',
                                  'list',
                                  'bullet',
                                  'indent',
                                  'align',
                                  'link',
                                  'image',
                                ]}
                                placeholder='Start typing your content here...'
                                className='[&_.ql-container]:min-h-[300px]'
                              />
                            </div>
                          )}
                        />
                      )}
                      {/* Marwari Editor */}
                      {activeLanguage === 'marwari' && (
                        <Controller
                          name='content.marwari'
                          control={control}
                          render={({ field }) => (
                            <div className='bg-white rounded-md'>
                              <ReactQuill
                                theme='snow'
                                value={field.value || ''}
                                onChange={field.onChange}
                                modules={quillModules}
                                formats={[
                                  'header',
                                  'bold',
                                  'italic',
                                  'underline',
                                  'strike',
                                  'list',
                                  'bullet',
                                  'indent',
                                  'align',
                                  'link',
                                  'image',
                                ]}
                                placeholder='Start typing your content here...'
                                className='[&_.ql-container]:min-h-[300px]'
                              />
                            </div>
                          )}
                        />
                      )}
                      {errors.content?.[activeLanguage] && (
                        <p className='text-red-400 text-xs mt-1'>
                          {errors.content[activeLanguage].message}
                        </p>
                      )}
                    </div>

                    {/* Short Description */}
                    <div>
                      <label className='block text-sm font-medium text-foreground mb-1'>
                        Short Description (
                        {
                          LANGUAGE_OPTIONS.find(
                            (l) => l.value === activeLanguage,
                          )?.label
                        }
                        )
                      </label>
                      <textarea
                        {...register(`shortDescription.${activeLanguage}`)}
                        rows={3}
                        placeholder={`Enter short description in ${
                          LANGUAGE_OPTIONS.find(
                            (l) => l.value === activeLanguage,
                          )?.label
                        }`}
                        className='w-full px-3 py-2 bg-input border border-border rounded-md text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent'
                      />
                    </div>
                  </div>

                  {/* SEO Settings */}
                  {/* <div className='bg-card border border-border rounded-lg p-6 space-y-4'>
                    <h4 className='text-md font-semibold text-foreground mb-4'>
                      SEO Settings
                    </h4>

                    <div>
                      <label className='block text-sm font-medium text-foreground mb-1'>
                        Meta Title
                      </label>
                      <input
                        {...register('seo.metaTitle')}
                        type='text'
                        placeholder='SEO meta title'
                        className='w-full px-3 py-2 bg-input border border-border rounded-md text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent'
                      />
                    </div>

                    <div>
                      <label className='block text-sm font-medium text-foreground mb-1'>
                        Meta Description
                      </label>
                      <textarea
                        {...register('seo.metaDescription')}
                        rows={3}
                        placeholder='SEO meta description'
                        className='w-full px-3 py-2 bg-input border border-border rounded-md text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent'
                      />
                    </div>

                    <div>
                      <label className='block text-sm font-medium text-foreground mb-1'>
                        OG Image URL
                      </label>
                      <input
                        {...register('seo.ogImage')}
                        type='text'
                        placeholder='Open Graph image URL'
                        className='w-full px-3 py-2 bg-input border border-border rounded-md text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent'
                      />
                    </div>
                  </div> */}
                </div>
              </div>
            </Panel>

            {/* Resize Handle */}
            <PanelResizeHandle className='w-2 bg-border hover:bg-primary transition-colors cursor-col-resize' />

            {/* Right Panel - Live Preview */}
            <Panel defaultSize={50} minSize={30}>
              <div className='h-full overflow-y-auto bg-muted/30'>
                <div className='sticky top-0 bg-card border-b border-border px-6 py-4 z-10'>
                  <div className='flex items-center gap-2'>
                    <Eye size={20} className='text-muted-foreground' />
                    <h4 className='text-md font-semibold text-foreground'>
                      Live Preview
                    </h4>
                    <span className='text-xs text-muted-foreground ml-2'>
                      (
                      {
                        LANGUAGE_OPTIONS.find((l) => l.value === activeLanguage)
                          ?.label
                      }
                      )
                    </span>
                  </div>
                </div>

                <div className='p-6'>
                  <div className='max-w-3xl mx-auto bg-card border border-border rounded-lg p-8 shadow-sm'>
                    {/* Preview Title */}
                    {watchTitle && (
                      <h1 className='text-3xl font-bold text-foreground mb-6 pb-4 border-b border-border'>
                        {watchTitle}
                      </h1>
                    )}

                    {/* Preview Content */}
                    {watchContent ? (
                      <div
                        className='prose prose-invert max-w-none [&_h1]:text-foreground [&_h2]:text-foreground [&_h3]:text-foreground [&_h4]:text-foreground [&_h5]:text-foreground [&_h6]:text-foreground [&_p]:text-card-foreground [&_li]:text-card-foreground [&_a]:text-primary [&_strong]:text-foreground'
                        dangerouslySetInnerHTML={{ __html: watchContent }}
                      />
                    ) : (
                      <div className='flex flex-col items-center justify-center h-64 text-muted-foreground'>
                        <Eye size={48} className='mb-4 opacity-50' />
                        <p className='text-lg'>No content to preview</p>
                        <p className='text-sm mt-2'>
                          Start typing to see the live preview
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Panel>
          </PanelGroup>
        </form>
      </div>
    </Layout>
  );
};

export default EditCmsPage;
